import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  type BoxProps,
  Button,
  Collapse,
  Flex,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  useControllableState,
  useSafeLayoutEffect,
} from '@chakra-ui/react';
import DOMpurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { type Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { IconRightArrowPurple } from '../Icons';

export interface MarkdownProps
  extends Options,
    Omit<BoxProps, 'children' | 'className'> {
  /**
   * The maximum number of lines to display before collapsing.
   * @default 3
   */
  maxLines?: number;
  /**
   * Whether the content is collapsed by default.
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * Whether the content is currently collapsed.
   */
  collapsed?: boolean;
  /**
   * Function to set the collapsed state.
   */
  setCollapsed?: (collapsed: boolean) => void;
  /**
   * should uncollapse initially based on amount of text.
   */
  initialResizeCb?: () => void;
  showMoreBgLayerProps?: BoxProps;
}

export const Markdown: React.FC<MarkdownProps> = ({
  children,
  maxLines = 3,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  setCollapsed: controlledSetCollapsed,
  initialResizeCb,
  showMoreBgLayerProps = {},
  ...rest
}) => {
  const [shouldTruncate, setShouldTruncate] = useState(true);
  const [lineHeight, setLineHeight] = useState(24);
  const [hasBeenResizedInInitialCheck, setHasBeenResizedInInitialCheck] =
    useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['common']);

  useSafeLayoutEffect(() => {
    if (contentRef.current) {
      const lineHeight = parseFloat(
        window.getComputedStyle(contentRef.current).lineHeight
      );
      setLineHeight(lineHeight);

      const maxHeight = lineHeight * maxLines;
      if (contentRef.current.scrollHeight > maxHeight) {
        setShouldTruncate(true);
      } else {
        setShouldTruncate(false);
      }
    }
  }, [children, maxLines]);

  const [collapsed, setCollapsed] = useControllableState<boolean>({
    value: controlledCollapsed,
    defaultValue: shouldTruncate ? defaultCollapsed : false,
    onChange: controlledSetCollapsed,
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (initialResizeCb && !shouldTruncate && !hasBeenResizedInInitialCheck) {
      setHasBeenResizedInInitialCheck(true);
      initialResizeCb();
    }

    // return () => setHasBeenResizedInInitialCheck(false);
  }, [hasBeenResizedInInitialCheck, initialResizeCb, shouldTruncate]);

  return (
    <Box pos="relative" display="flex" flexDir="column" {...rest}>
      <Collapse
        startingHeight={
          shouldTruncate
            ? collapsed
              ? `${maxLines * lineHeight}px`
              : 'auto'
            : 'auto'
        }
        in={shouldTruncate ? !collapsed : true}
      >
        <Box ref={contentRef} px={1}>
          <ReactMarkdown
            components={{
              ol: ({ node: _, ...rest }) => <OrderedList {...rest} />,
              ul: ({ node: _, ...rest }) => <UnorderedList {...rest} />,
              li: ({ node: _, ...rest }) => <ListItem {...rest} />,
              p: ({ node: _, ...rest }) => <Text {...rest} />,
            }}
            rehypePlugins={[rehypeRaw]}
            {...rest}
          >
            {DOMpurify.sanitize(children ?? '')}
          </ReactMarkdown>
        </Box>
      </Collapse>

      {shouldTruncate && (
        <Flex
          pos={collapsed ? 'static' : 'sticky'}
          right={collapsed ? 'auto' : 1}
          bottom={collapsed ? 'auto' : 1}
        >
          {collapsed && (
            <Box
              pos="absolute"
              bottom="-1"
              right={0}
              left={0}
              height="16"
              bg="linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)"
              {...showMoreBgLayerProps}
            />
          )}
          <Button
            size="sm"
            variant="link"
            color="brand.500"
            ml="auto"
            mr={1}
            rightIcon={
              <IconRightArrowPurple
                fontSize="larger"
                transform={collapsed ? 'rotate(90deg)' : 'rotate(-90deg)'}
              />
            }
            mt={2}
            onClick={toggleCollapsed}
          >
            {collapsed
              ? t('common:actions.showMore')
              : t('common:actions.showLess')}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
